import { authConfig } from '@/auth/auth.config'
import { deriveCodeChallenge, generateCodeVerifier, generateState } from '@/auth/pkce'
import { buildAuthorizeUrl } from '@/services/auth.service'

/**
 * The SSO test-login popup (TO-308, REQ-006).
 *
 * The opener keeps the PKCE verifier and `state` IN MEMORY, opens the hosted UI
 * in a named popup pinned to the tenant's IdP, and waits for the popup's
 * `/auth/callback` to post back the authorization code. The opener then sends
 * the code to the server, which exchanges it and checks the identity's
 * provider. The browser never decides whether the test passed, and the popup
 * never starts a session of its own.
 */

/** `window.name` of the test-login popup, so its /auth/callback knows to hand the code back. */
export const SSO_TEST_WINDOW_NAME = 'today-sso-test'

/** The `postMessage` type the popup sends to its opener. */
export const SSO_TEST_MESSAGE = 'today:sso-test-callback'

/** How often the opener checks whether the admin closed the popup. */
const CLOSED_POLL_MS = 500

/** What the opener needs to prove the test login server-side. */
export interface SsoTestCallback {
	code: string
	codeVerifier: string
	redirectUri: string
}

interface SsoTestMessage {
	type: typeof SSO_TEST_MESSAGE
	code: string
	state: string
}

const isSsoTestMessage = (data: unknown): data is SsoTestMessage => {
	const m = data as Partial<SsoTestMessage> | null
	return m?.type === SSO_TEST_MESSAGE && typeof m.code === 'string' && typeof m.state === 'string'
}

/**
 * Run the test login in a popup and resolve with the code to verify.
 * Only a same-origin message carrying this attempt's `state` is accepted.
 *
 * @throws When the popup is blocked or closed before it reports back.
 */
export async function runSsoTestLogin(identityProvider: string, win: Window = window): Promise<SsoTestCallback> {
	const codeVerifier = generateCodeVerifier()
	const state = generateState()
	const challenge = await deriveCodeChallenge(codeVerifier)

	const popup = win.open(buildAuthorizeUrl({ challenge, state, identityProvider }), SSO_TEST_WINDOW_NAME, 'popup')
	if (!popup) throw new Error('The test-login popup was blocked. Allow pop-ups for this site and try again.')

	return new Promise<SsoTestCallback>((resolve, reject) => {
		const cleanup = () => {
			win.removeEventListener('message', onMessage)
			win.clearInterval(closedPoll)
		}
		const onMessage = (event: MessageEvent) => {
			if (event.origin !== win.location.origin || !isSsoTestMessage(event.data)) return
			if (event.data.state !== state) return
			cleanup()
			resolve({ code: event.data.code, codeVerifier, redirectUri: authConfig.redirectUri })
		}
		const closedPoll = win.setInterval(() => {
			if (!popup.closed) return
			cleanup()
			reject(new Error('The test-login popup was closed before sign-in completed.'))
		}, CLOSED_POLL_MS)
		win.addEventListener('message', onMessage)
	})
}

/**
 * The popup side of `/auth/callback`. Returns `true` when this window is the
 * test-login popup: the code and `state` go to the opener (same origin only)
 * and the popup closes. Returns `false` in an ordinary tab, which completes a
 * normal login.
 */
export function forwardSsoTestCallback(params: URLSearchParams, win: Window = window): boolean {
	if (win.name !== SSO_TEST_WINDOW_NAME || !win.opener) return false
	const message: SsoTestMessage = {
		type: SSO_TEST_MESSAGE,
		code: params.get('code') ?? '',
		state: params.get('state') ?? '',
	}
	;(win.opener as Window).postMessage(message, win.location.origin)
	win.close()
	return true
}
